import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import Popup from "../common/popup";
import { dateformatwithuktime } from "../utilityfunctions";
import { useTranslation } from "react-i18next";
import moment from "moment";
import * as XLSX from "sheetjs-style";
import { canAction } from "../common/permissionhelper";

export const EmailReport = ({}) => {
  const { t } = useTranslation();

  const emailTypeMap = {
    logged_in_not_order: t("logged_in_ordered_email_txt"),
    logged_in_not_order_cutoff: t("logged_in_ordered_email_cutoff_txt"),
    abandoned_cart: t("abandoned_cart_txt"),
  };

  const customStyles = {
    rows: { style: { minHeight: "25px" } },
    headCells: { style: { padding: "8px" } },
    cells: { style: { padding: "8px" } },
  };

  const paginationComponentOptions = {
    rowsPerPageText: "",
    rangeSeparatorText: "",
    selectAllRowsItem: true,
    selectAllRowsItemText: "",
    noRowsPerPage: "",
  };

  const [isLoading, setIsLoading] = useState(true);
  const [logLoading, setLogLoading] = useState(false);
  const [selectedemailtype, setSelectedemailtype] = useState("");
  const [campaignData, setCampaignData] = useState([]);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportAlert, setShowExportAlert] = useState(false);
  const [showNoDataAlert, setShowNoDataAlert] = useState(false);
  const [searchCampaign, setSearchCampaign] = useState("");

  const fetchCampaignDetails = () => {
    setIsLoading(true);
    const URL = BaseUrl + "admin/emailcampaignwisereport";
    http
      .get(URL)
      .then((res) => {
        setCampaignData(res.data?.data || []);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchCampaignDetails();
  }, []);
  // filtered data based on type and search
  const filteredCampaignData = useMemo(() => {
    return campaignData.filter((item) => {
      const matchesType =
        !selectedemailtype || item.emailType === selectedemailtype;
      const matchesSearch =
        !searchCampaign ||
        item.emailCampaignName
          ?.toLowerCase()
          .includes(searchCampaign.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [campaignData, selectedemailtype, searchCampaign]);
  // summary from filtered data
  const summaryEmailsSent = useMemo(
    () =>
      filteredCampaignData.reduce(
        (sum, c) => sum + (c.totalEmailsSent || 0),
        0,
      ),
    [filteredCampaignData],
  );
  // summary from filtered data
  const summaryOrders = useMemo(
    () =>
      filteredCampaignData.reduce(
        (sum, c) => sum + (c.campaignOrdersCount || 0),
        0,
      ),
    [filteredCampaignData],
  );

  const summaryConvertedCustomers = useMemo(
    () =>
      filteredCampaignData.reduce(
        (sum, c) => sum + (c.convertedCustomerCount || 0),
        0,
      ),
    [filteredCampaignData],
  );

  const summaryRevenue = useMemo(
    () =>
      filteredCampaignData.reduce(
        (sum, c) => sum + (c.campaignRevenue || 0),
        0,
      ),
    [filteredCampaignData],
  );
  // table columns
  const campaignColumns = [
    {
      name: t("campaign_namee_txt"),
      selector: (row) => row?.emailCampaignName,
      sortable: true,
    },
    {
      name: t("email_type_txt"),
      selector: (row) => emailTypeMap[row.emailType] || row.emailType,
      sortable: true,
    },
    {
      name: t("cutoff_time_txt"),
      selector: (row) => row?.cutoffTime,
      sortable: true,
    },
    {
      name: t("campaign_mode_status_txt"),
      selector: (row) => {
        const mode = row?.autoEmail ? t("auto_txt") : t("manual_txt");
        let status = "";
        if (row?.status) {
          const s = row.status.toLowerCase();
          const r = row.runStatus?.toLowerCase();
          if (s === "scheduled" && r === "run") status = t("running_txt");
          else if (s === "scheduled" && r === "pause") status = t("paused_txt");
          else if (s === "scheduled" && r === "stop")
            status = t("completed_txt");
          else status = s.charAt(0).toUpperCase() + s.slice(1);
        }
        return `${mode} / ${status}`;
      },
      sortable: true,
    },
    {
      name: t("total_targeted_customers_count_txt"),
      selector: (row) =>
        row?.customerCount && row.customerCount !== 0 ? row.customerCount : "-",
      sortable: true,
    },
    {
      name: t("total_email_sent_count_txt"),
      selector: (row) =>
        row?.totalEmailsSent && row.totalEmailsSent !== 0
          ? row.totalEmailsSent
          : "-",
      sortable: true,
    },
    {
      name: t("total_order(s)_txt"),
      selector: (row) =>
        row?.campaignOrdersCount && row.campaignOrdersCount !== 0
          ? row.campaignOrdersCount
          : "-",
      sortable: true,
    },
    {
      name: t("total_converted_customers_txt"),
      selector: (row) =>
        row?.convertedCustomerCount && row.convertedCustomerCount !== 0
          ? row.convertedCustomerCount
          : "-",
      sortable: true,
    },
    {
      name: t("total_revenue_summary_txt"),
      selector: (row) =>
        row?.campaignRevenue && row.campaignRevenue !== 0
          ? `£${row.campaignRevenue.toFixed(2)}`
          : "-",
      sortable: true,
    },
    {
      name: t("logs_txt"),
      selector: (row) => {
        const handleClick = (e) => {
          e.preventDefault();
          handleViewLogs(row);
        };
        return (
          <a
            href="#"
            className="title-tooltip title-up button"
            onClick={handleClick}
            style={{ cursor: "pointer", pointerEvents: "auto" }}
          >
            {t("view_log_txt")}
          </a>
        );
      },
    },
  ];
  // fetch campaign account details for logs
  const fetchCampaignAccountDetails = async (campaignId) => {
    try {
      setLogLoading(true);
      const response = await http.get(
        `${BaseUrl}admin/emailreport/customers/${campaignId}`,
      );
      setLogsData(response.data?.data?.accounts || []);
      setLogLoading(false);
    } catch (err) {
      console.error("Error fetching logs:", err);
      setLogLoading(false);
    }
  };
  // handle view logs
  const handleViewLogs = (row) => {
    setSelectedCampaign(row);
    fetchCampaignAccountDetails(row?.campaignId);
    setLogsModalOpen(true);
  };
  // log table columns
  const campaignLogColumns = [
    {
      name: t("account_code_txt"),
      selector: (row) => row.accountCode,
      sortable: true,
    },
    {
      name: t("total_email_sent_count_txt"),
      selector: (row) => row.totalEmailsSent || "-",
      sortable: true,
    },
    {
      name: t("last_email_sent_date"),
      selector: (row) =>
        row.lastEmailSentDate
          ? dateformatwithuktime(row.lastEmailSentDate)
          : "-",
      sortable: true,
    },
    {
      name: t("total_revenue_summary_txt"),
      selector: (row) =>
        row.revenue ? `£${Number(row.revenue).toFixed(2)}` : "-",
      sortable: true,
    },
    {
      name: t("order_number_txt"),
      selector: (row) => row.firstOrderNumber || "-",
      sortable: true,
    },
    {
      name: t("order_date_txt"),
      selector: (row) =>
        row.firstOrderDate ? dateformatwithuktime(row.firstOrderDate) : "-",
      sortable: true,
    },
    {
      name: t("cus_email_activity_txt"),
      selector: (row) => {
        if (row?.customerStatus) {
          let dispalyStatus = row.customerStatus;
          let status = row.customerStatus;

          if (status === "deactive") {
            dispalyStatus = t("deactivated_txt");
          } else if (status === "website_access_disabled") {
            dispalyStatus = t("website_access_disabled_txt");
          } else if (status === "reminder_limit_reached") {
            dispalyStatus = t("completed_txt");
          } else if (status === "unsubscribed") {
            dispalyStatus = t("unsubscribed_txt");
          } else if (row.emailSentStatus === "bounced") {
            dispalyStatus = t("bounced_txt");
          } else if (status === "restricted_account") {
            dispalyStatus = t("restricted_account_status_txt");
          } else if (status === "cart_empty") {
            dispalyStatus = t("cart_empty_txt");
          } else if (status === "no_empty_cart") {
            dispalyStatus = t("no_empty_cart_txt");
          } else if (status === "active" && row.runStatus === "pause") {
            dispalyStatus = t("pause_txt");
          } else if (status === "active" && row.runStatus === "run") {
            dispalyStatus = t("running_txt");
          }
          const shouldAppendStopped =
            status !== "reminder_limit_reached" &&
            !(status === "active" && row.runStatus === "run");
          if (shouldAppendStopped) {
            dispalyStatus += ` (${t("stopped_txt")})`;
          }
          return dispalyStatus.charAt(0).toUpperCase() + dispalyStatus.slice(1);
        }
        return "-";
      },
      sortable: true,
    },
  ];
  // export to excel handlers
  const handleConfirmExportToExcel = () => {
    if (!filteredCampaignData || filteredCampaignData.length === 0) {
      setShowNoDataAlert(true);
      return;
    }
    setShowExportAlert(true);
  };

  const confirmExport = () => {
    setIsExporting(true);
    setShowExportAlert(false);
    handleExportToExcel();
  };
  // export to excel function
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const campaignDataForExport = [...filteredCampaignData];
      // 1. Email Type Label
      const typeLabel = selectedemailtype
        ? `${emailTypeMap[selectedemailtype]} ${t("campaign_details_txt")}`
        : t("all_email_type_campaign_details_txt");
      // 2. Search Label (Grouped by)
      const searchLabel = searchCampaign
        ? ` | ${t("grouped_by_txt")}: ${searchCampaign}`
        : "";
      // 3. Total Records
      const totalLabel = ` | ${t("total_records_txt")}: ${filteredCampaignData.length}`;
      // FINAL TITLE
      const finalTitle = `${typeLabel}${searchLabel}${totalLabel}`;
      const wb = XLSX.utils.book_new();
      const emptyRows = Array(5).fill([]);
      const noDataRow = [
        {
          v: t("no_records_available_txt"),
          s: {
            font: {
              bold: true,
              sz: 14,
              italic: true,
              color: { rgb: "FF0000" },
            },
            alignment: { horizontal: "center" },
          },
        },
      ];
      // Campaign Details Sheet
      const campaignHeaders = [
        t("campaign_namee_txt"),
        t("email_type_txt"),
        t("cutoff_time_txt"),
        t("campaign_mode_status_txt"),
        t("total_targeted_customers_count_txt"),
        t("total_email_sent_count_txt"),
        t("total_order(s)_txt"),
        t("total_converted_customers_txt"),
        t("total_revenue_summary_txt"),
      ];
      const campaignRows = campaignDataForExport.map((row) => {
        const mode = row?.autoEmail ? t("auto_txt") : t("manual_txt");
        let status = "";
        if (row?.status) {
          const s = row.status.toLowerCase();
          const r = row.runStatus?.toLowerCase();
          if (s === "scheduled" && r === "run") status = t("running_txt");
          else if (s === "scheduled" && r === "pause") status = t("paused_txt");
          else if (s === "scheduled" && r === "stop")
            status = t("completed_txt");
          else status = s.charAt(0).toUpperCase() + s.slice(1);
        }
        return [
          row.emailCampaignName || "-",
          emailTypeMap[row.emailType] || row.emailType,
          row.cutoffTime || "-",
          `${mode} / ${status}`,
          row.customerCount || 0,
          row.totalEmailsSent || 0,
          row.campaignOrdersCount || 0,
          row.convertedCustomerCount || 0,
          row.campaignRevenue ? `£${row.campaignRevenue.toFixed(2)}` : "£0.00",
        ];
      });
      const summaryRows = [
        [
          {
            v: `${t("total_email_sent_txt")}: ${summaryEmailsSent > 0 ? summaryEmailsSent : t("na_txt")}`,
            s: { font: { bold: true }, alignment: { horizontal: "left" } },
          },
        ],
        [
          {
            v: `${t("total_order(s)_txt")}: ${summaryOrders > 0 ? summaryOrders : t("na_txt")}`,
            s: { font: { bold: true }, alignment: { horizontal: "left" } },
          },
        ],
        [
          {
            v: `${t("total_converted_customers_txt")}: ${summaryConvertedCustomers > 0 ? summaryConvertedCustomers : t("na_txt")}`,
            s: { font: { bold: true }, alignment: { horizontal: "left" } },
          },
        ],
        [
          {
            v: `${t("total_revenue_summary_txt")}: ${summaryRevenue > 0 ? `£${summaryRevenue.toFixed(2)}` : t("na_txt")}`,
            s: { font: { bold: true }, alignment: { horizontal: "left" } },
          },
        ],
      ];
      const campaignSheetData =
        campaignDataForExport.length > 0
          ? [
              ...summaryRows,
              [],
              [
                {
                  v: finalTitle,
                  s: {
                    font: { bold: true },
                    alignment: { horizontal: "left" },
                  },
                },
              ],
              campaignHeaders.map((h) => ({
                v: h,
                s: { font: { bold: true }, alignment: { horizontal: "left" } },
              })),
              ...campaignRows,
            ]
          : [
              ...summaryRows,
              [],
              [
                {
                  v: finalTitle,
                  s: {
                    font: { bold: true },
                    alignment: { horizontal: "left" },
                  },
                },
              ],
              [],
              ...emptyRows,
              noDataRow,
            ];
      const wsCampaign = XLSX.utils.aoa_to_sheet(campaignSheetData);
      wsCampaign["!merges"] = [
        { s: { r: 5, c: 0 }, e: { r: 5, c: campaignHeaders.length - 1 } },
      ];
      if (campaignDataForExport.length === 0) {
        wsCampaign["!merges"].push({
          s: { r: 2 + emptyRows.length, c: 0 },
          e: { r: 2 + emptyRows.length, c: campaignHeaders.length - 1 },
        });
      }
      wsCampaign["!cols"] = campaignHeaders.map((h, i) => {
        let maxLength = h.length;
        campaignRows.forEach((r) => {
          maxLength = Math.max(maxLength, String(r[i]).length);
        });
        return { wch: maxLength + 5 };
      });
      XLSX.utils.book_append_sheet(wb, wsCampaign, t("campaign_details_txt"));
      // Save Excel
      const exportDate = moment().format("DD-MM-YYYY");
      XLSX.writeFile(wb, `EmailReport-${exportDate}.xlsx`);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };
  // SORT logs data by orderNumber (asc), then by accountCode (A to Z)
  const sortedLogsData = useMemo(() => {
    return [...logsData].sort((a, b) => {
      const orderA = a.firstOrderNumber ? Number(a.firstOrderNumber) : 0;
      const orderB = b.firstOrderNumber ? Number(b.firstOrderNumber) : 0;
      if (orderA !== orderB) return orderA - orderB;
      const accA = a.accountCode || "";
      const accB = b.accountCode || "";
      return accA.localeCompare(accB);
    });
  }, [logsData]);

  return (
    <div
      className={`chart-container email-report-table ${
        isLoading ? "loading-buffer" : ""
      }`}
      style={{ verticalAlign: "top" }}
    >
      <div className="chart-Hdr">
        <h2>{t("auto_email_performance_txt")}</h2>
        <div className="chart-fltr">
          {canAction("manage_dashboard.emailreport", "export") && (
            <div className="prod-import-btn odr-expo-btn">
              <a
                data-title={t("export_filtered_records")}
                className="export-btn button title-tooltip title-left"
                onClick={handleConfirmExportToExcel}
              >
                {t("export_btn_txt")}
              </a>
            </div>
          )}
          <div className="chart-fltr" style={{ width: "200px" }}>
            <input
              type="text"
              value={searchCampaign}
              onChange={(e) => setSearchCampaign(e.target.value)}
              placeholder={t("search_campaign_name_txt")}
            />
          </div>
          <select
            value={selectedemailtype}
            onChange={(e) => setSelectedemailtype(e.target.value)}
          >
            <option value="">{t("all_txt")}</option>
            {Object.entries(emailTypeMap).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {/* Summary Cards */}
        {filteredCampaignData.length > 0 && (
          <div className="fltr-sub-val flex-bx-wrap col4">
            <div className="statistics summary-cards">
              <div className="card">
                <strong>{t("total_email_sent_txt")}</strong>
                <h3>{summaryEmailsSent.toLocaleString()}</h3>
              </div>
            </div>
            <div className="statistics summary-cards">
              <div className="card">
                <strong>{t("total_order(s)_txt")}</strong>
                <h3>
                  {summaryOrders > 0
                    ? summaryOrders.toLocaleString()
                    : t("na_txt")}
                </h3>
              </div>
            </div>
            <div className="statistics summary-cards">
              <div className="card">
                <strong>{t("total_converted_customers_txt")}</strong>
                <h3>
                  {summaryConvertedCustomers > 0
                    ? summaryConvertedCustomers.toLocaleString()
                    : t("na_txt")}
                </h3>
              </div>
            </div>
            <div className="statistics summary-cards">
              <div className="card">
                <strong>{t("total_revenue_summary_txt")}</strong>
                <h3>
                  {summaryRevenue > 0
                    ? `£${summaryRevenue.toFixed(2).toLocaleString()}`
                    : t("na_txt")}
                </h3>
              </div>
            </div>
          </div>
        )}
        {filteredCampaignData.length > 0 && (
          <div className="fltr-sub-val">
            <span className="fltr-val">
              {t("total_record(s)_txt")}{" "}
              <strong>{filteredCampaignData.length}</strong>
            </span>
          </div>
        )}
      </div>
      <DataTable
        columns={campaignColumns}
        data={filteredCampaignData}
        customStyles={customStyles}
        noDataComponent={
          <div className="no-records-found">
            {t("no_data_filtered_search_txt")}
          </div>
        }
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 40, 60, 80, 100]}
        paginationComponentOptions={paginationComponentOptions}
      />
      {logsModalOpen && (
        <Popup
          extClass="order-pop customer-list-pop email-report-view-log-pop bordered"
          headMoreTxt={
            <>
              <div className="order-top">
                <div className="bottom order-flex">
                  {/* LEFT SECTION */}
                  <div className="order_account_details abandoned-cart-left">
                    <p>
                      {" "}
                      {t("campaign_name_txt")}:{" "}
                      <span className="date">
                        {selectedCampaign?.emailCampaignName}
                      </span>
                    </p>
                    <p>
                      {" "}
                      {t("email_type_txt")}:{" "}
                      <span className="date">
                        {emailTypeMap[selectedCampaign?.emailType] ||
                          t("na_txt")}
                      </span>
                    </p>
                    <p>
                      {" "}
                      {t("cutoff_time_txt")}:{" "}
                      <span className="date">
                        {selectedCampaign?.cutoffTime || t("na_txt")}
                      </span>
                    </p>
                    <p>
                      {" "}
                      {t("campaign_mode_status_txt")}:{" "}
                      <span className="date">
                        {(selectedCampaign?.autoEmail
                          ? t("auto_txt")
                          : t("manual_txt")) +
                          " / " +
                          (() => {
                            const s = selectedCampaign?.status?.toLowerCase();
                            const r =
                              selectedCampaign?.runStatus?.toLowerCase();
                            if (s === "scheduled" && r === "run")
                              return t("running_txt");
                            if (s === "scheduled" && r === "pause")
                              return t("paused_txt");
                            if (s === "scheduled" && r === "stop")
                              return t("completed_txt");
                            return s
                              ? s.charAt(0).toUpperCase() + s.slice(1)
                              : "-";
                          })()}
                      </span>
                    </p>
                    <p>
                      {t("total_targeted_customers_txt")}:{" "}
                      <span className="date">
                        {selectedCampaign?.customerCount || t("na_txt")}
                      </span>
                    </p>
                  </div>
                  {/* RIGHT SECTION */}
                  <div className="order_account_details">
                    <p>
                      {" "}
                      {t("total_email_sent_txt")}:{" "}
                      <span className="date">
                        {selectedCampaign?.totalEmailsSent}
                      </span>
                    </p>
                    <p>
                      {" "}
                    {t("total_order(s)_txt")}:{" "}
                      <span className="date">
                        {selectedCampaign?.campaignOrdersCount > 0
                          ? selectedCampaign?.campaignOrdersCount
                          : t("nil_txt")}
                      </span>
                    </p>
                    <p>
                      {" "}
                      {t("total_revenue_summary_txt")}:{" "}
                      <span className="date">
                        {selectedCampaign?.campaignRevenue > 0
                          ? `£${selectedCampaign?.campaignRevenue?.toFixed(2)}`
                          : t("nil_txt")}
                      </span>
                    </p>
                    <p>
                      {" "}
                      {t("total_converted_customers_txt")}:{" "}
                      <span className="date">
                        {selectedCampaign?.convertedCustomerCount > 0
                          ? selectedCampaign?.convertedCustomerCount
                          : t("nil_txt")}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </>
          }
          closebtn={
            <div className="pop-header-top order-top">
              <div className="head">
                <h4>{t("campaign_details_txt")}</h4>
              </div>
              <span
                className="close"
                onClick={() => setLogsModalOpen(false)}
              ></span>
            </div>
          }
          bodyMoreTxt={
            <>
              {/* NOTE */}
              <div className="input-field">
                <div className="note-text">
                  <strong>{t("campaign_revenue_note_txt")}</strong>
                </div>
              </div>
              <div className="modal-table">
                {logLoading ? (
                  <div className="loading-overlay">
                    <div className="loading-message">{t("loading_txt")}</div>
                  </div>
                ) : (
                  <div className="checkout-table">
                    <div className="table_overflow">
                      {logsData.length > 0 ? (
                        <DataTable
                          columns={campaignLogColumns}
                          data={sortedLogsData}
                          pagination
                          customStyles={customStyles}
                          noDataComponent={
                            <div className="no-records-found">
                              {t("no_data_filtered_search_txt")}
                            </div>
                          }
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25, 50, 75, 100]}
                          paginationComponentOptions={
                            paginationComponentOptions
                          }
                        />
                      ) : (
                        <div className="no-records-found">
                          {t("no_record_txt")}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          }
        />
      )}
      {isExporting && (
        <div className="loading-full-bg">
          <div className="loading-buffer"></div>
        </div>
      )}
      {showExportAlert && (
        <div className="custom-alert-pop paylater-popup">
          <div className="custom-alert-container">
            <div className="custom-alert-wrap">
              <div className="custom-alert-header">
                <h4>{t("alert_txt")}</h4>
              </div>
              <div className="custom-alert-body">
                <p>{t("export_confirm_txt")}</p>
                <div className="alert-buttons">
                  <a onClick={confirmExport} className="popup-alert-btn button">
                    {t("ok_txt")}
                  </a>
                  <a
                    onClick={() => setShowExportAlert(false)}
                    className="popup-alert-btn button"
                  >
                    {t("cancel_txt")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showNoDataAlert && (
        <div className="custom-alert-pop paylater-popup">
          <div className="custom-alert-container">
            <div className="custom-alert-wrap">
              <div className="custom-alert-header">
                <h4>{t("notice_txt")}</h4>
              </div>
              <div className="custom-alert-body">
                <p>{t("no_records_for_export_txt")}</p>
                <div className="alert-buttons">
                  <a
                    onClick={() => setShowNoDataAlert(false)}
                    className="popup-alert-btn button"
                  >
                    {t("ok_txt")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
